import type { SvgIconComponent } from '@mui/icons-material'
import {
  Assessment,
  AssessmentOutlined,
  Ballot,
  BallotOutlined,
  CalendarMonth,
  CalendarMonthOutlined,
  CreditCard,
  CreditCardOutlined,
  Dashboard,
  DashboardOutlined,
  DisplaySettings,
  DisplaySettingsOutlined,
  EmojiEvents,
  EmojiEventsOutlined,
  Forum,
  ForumOutlined,
  GroupWork,
  GroupWorkOutlined,
  Layers,
  LayersOutlined,
  Map as MapIcon,
  MapOutlined,
  People,
  PeopleOutlined,
  QueryStats,
  QueryStatsOutlined,
} from '@mui/icons-material'
import type { TFunction } from 'i18next'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { matchPath } from 'react-router'
import { useLocation } from 'react-router-dom'
import { FLAGS } from '../../../constants'
import useCurrentGameRoute from '../../../hooks/useCurrentGameRoute'
import {
  hasPermission,
  type Permission,
} from '../../../services/auth/permissions/permissions'
import {
  useGameAccess,
  useHasAdminManagementAccess,
  useHasGlobalModulesAccess,
} from '../../../services/auth/permissions/usePermissions'
import useAuth from '../../../services/auth/useAuth'

type NavigationItem = {
  title: string
  icon?: SvgIconComponent
  activeIcon?: SvgIconComponent
  path?: string
  children?: NavigationItem[]
  disabled?: boolean
}

type Navigation = {
  items: NavigationItem[]
  disabled?: boolean
}

interface GameNavigationPermissions {
  analytics: boolean
  cards: boolean
  events: boolean
  leaderboards: boolean
  installer: boolean
  locations: boolean
  locationsWrite: boolean
  messages: boolean
  overview: boolean
  players: boolean
  prizes: boolean
  resources: boolean
  settings: boolean
  surveys: boolean
  teams: boolean
}

const filterNavigationItems = (items: NavigationItem[]): NavigationItem[] => {
  return items.reduce<NavigationItem[]>((visibleItems, item) => {
    if (item.disabled) {
      return visibleItems
    }

    const children = item.children
      ? filterNavigationItems(item.children)
      : undefined

    if (item.children && children?.length === 0) {
      return visibleItems
    }

    visibleItems.push({
      ...item,
      children,
    })

    return visibleItems
  }, [])
}

const messageCentreItems = (
  t: TFunction,
  prefix: string,
  permissions: GameNavigationPermissions
): Navigation => ({
  items: [
    {
      title: t('navigation.engagement'),
      icon: ForumOutlined,
      activeIcon: Forum,
      disabled: !permissions.messages,
      children: [
        {
          title: t('navigation.messages'),
          path: `${prefix}/engagement/messages`,
        },
        {
          title: t('navigation.teamMessages'),
          path: `${prefix}/engagement/team-messages`,
        },
      ],
    },
  ],
})

const mapSection = (
  t: TFunction,
  prefix: string,
  permissions: GameNavigationPermissions
): Navigation => ({
  items: [
    {
      title: t('navigation.map'),
      icon: MapOutlined,
      activeIcon: MapIcon,
      path: `${prefix}/map`,
      children: [
        {
          title: t('navigation.mapStatus'),
          path: `${prefix}/map/status`,
          disabled: !permissions.locationsWrite,
        },
        {
          title: t('navigation.locations'),
          path: `${prefix}/map/locations`,
          disabled: !permissions.locations,
        },
        {
          title: t('navigation.mapGroups'),
          path: `${prefix}/map/groups`,
          disabled: !permissions.locations,
        },
        {
          title: t('navigation.mapDatasets'),
          path: `${prefix}/map/datasets`,
          disabled: !permissions.locations,
        },
        {
          title: t('navigation.mapInstaller'),
          path: `${prefix}/map/installer`,
          disabled: !permissions.installer,
        },
        {
          title: t('navigation.mapSettings'),
          path: `${prefix}/map/settings`,
          disabled: !permissions.locations,
        },
      ],
    },
  ],
})

const generalSections = (
  t: TFunction,
  prefix: string,
  hasGlobalModulesAccess: boolean
): Navigation[] => [
  {
    items: [
      {
        title: t('navigation.dashboard'),
        path: `/`,
        icon: DashboardOutlined,
        activeIcon: Dashboard,
      },
      {
        title: t('navigation.games'),
        path: `/games`,
        icon: GroupWorkOutlined,
        activeIcon: GroupWork,
      },
      {
        title: t('navigation.players'),
        path: `/players`,
        icon: PeopleOutlined,
        activeIcon: People,
        disabled: !hasGlobalModulesAccess,
      },
      {
        title: t('navigation.cardOrders'),
        path: `/card-orders`,
        icon: CreditCardOutlined,
        activeIcon: CreditCard,
        disabled: !hasGlobalModulesAccess,
      },
      {
        title: t('navigation.dataLibrary'),
        path: `/data-library`,
        icon: LayersOutlined,
        activeIcon: Layers,
        disabled: !hasGlobalModulesAccess,
      },
    ],
  },
]

const generalSectionPartner = (
  t: TFunction,
  hasGlobalModulesAccess: boolean
): Navigation[] => [
  {
    items: [
      {
        title: t('navigation.games'),
        path: `/games`,
        icon: GroupWorkOutlined,
        activeIcon: GroupWork,
      },
      {
        title: t('navigation.players'),
        path: `/players`,
        icon: PeopleOutlined,
        activeIcon: People,
        disabled: !hasGlobalModulesAccess,
      },
    ],
  },
]

const generalSectionEmpty = (t: TFunction): Navigation[] => [
  {
    items: [
      {
        title: t('navigation.games'),
        path: `/games`,
        icon: GroupWorkOutlined,
        activeIcon: GroupWork,
      },
    ],
  },
]

const gameSections = (
  t: TFunction,
  prefix: string,
  permissions: GameNavigationPermissions
): Navigation[] => {
  return [
    {
      items: [
        {
          title: t('navigation.overview'),
          path: `${prefix}`,
          icon: DashboardOutlined,
          activeIcon: Dashboard,
          disabled: !permissions.overview,
        },
      ],
    },
    {
      items: [
        {
          title: t('navigation.analytics'),
          icon: QueryStatsOutlined,
          activeIcon: QueryStats,
          disabled: !permissions.analytics,
          children: [
            {
              title: t('navigation.demographics'),
              path: `${prefix}/demographics`,
            },
            {
              title: t('navigation.teams'),
              path: `${prefix}/analytics-teams`,
            },
            {
              title: t('navigation.activity'),
              path: `${prefix}/activity`,
            },
            {
              title: t('navigation.mapping'),
              path: `${prefix}/mapping-analytics`,
            },
            {
              title: `${t('navigation.mapping')} v2`,
              path: `${prefix}/mapping-v2`,
              disabled: !FLAGS.MAPPING_V2,
            },
          ],
        },
      ],
    },
    {
      items: [
        {
          title: t('navigation.events'),
          path: `${prefix}/events`,
          icon: CalendarMonthOutlined,
          activeIcon: CalendarMonth,
          disabled: !permissions.events,
        },
      ],
    },
    {
      items: [
        {
          title: t('navigation.surveys'),
          path: `${prefix}/surveys`,
          icon: BallotOutlined,
          activeIcon: Ballot,
          disabled: !permissions.surveys,
        },
      ],
    },
    {
      items: [
        {
          title: t('navigation.prizes'),
          icon: EmojiEventsOutlined,
          activeIcon: EmojiEvents,
          disabled: !permissions.prizes,
          children: [
            {
              title: t('navigation.prizeDraws'),
              path: `${prefix}/prizes/prize-draws`,
              disabled: !FLAGS.PRIZE_DRAWS,
            },
            {
              title: t('navigation.luckyTaps'),
              path: `${prefix}/prizes/lucky-taps`,
            },
          ],
        },
      ],
    },
    mapSection(t, prefix, permissions),
    {
      items: [
        {
          title: t('navigation.setup'),
          icon: DisplaySettingsOutlined,
          activeIcon: DisplaySettings,
          children: [
            {
              title: t('navigation.players'),
              path: `${prefix}/players`,
              disabled: !permissions.players,
            },
            {
              title: t('navigation.teams'),
              path: `${prefix}/teams`,
              disabled: !permissions.teams,
            },
            {
              title: t('navigation.cards'),
              path: `${prefix}/cards/game-ranges`,
              disabled: !permissions.cards,
            },
            {
              title: t('navigation.resources'),
              path: `${prefix}/resources`,
              disabled: !permissions.resources,
            },
            {
              title: t('navigation.leaderboards'),
              path: `${prefix}/leaderboards`,
              disabled: !permissions.leaderboards,
            },
            {
              title: t('navigation.settings'),
              path: `${prefix}/general`,
              disabled: !permissions.settings,
            },
          ],
        },
      ],
    },
  ]
}

const gameSectionsPartner = (
  t: TFunction,
  prefix: string,
  permissions: GameNavigationPermissions
): Navigation[] => {
  return [
    {
      items: [
        {
          title: t('navigation.overview'),
          path: `${prefix}`,
          icon: AssessmentOutlined,
          activeIcon: Assessment,
          disabled: !permissions.overview,
        },
      ],
    },
    {
      items: [
        {
          title: t('navigation.analytics'),
          icon: QueryStatsOutlined,
          activeIcon: QueryStats,
          disabled: !permissions.analytics,
          children: [
            {
              title: t('navigation.demographics'),
              path: `${prefix}/demographics`,
            },
            {
              title: t('navigation.teams'),
              path: `${prefix}/analytics-teams`,
            },
            {
              title: t('navigation.activity'),
              path: `${prefix}/activity`,
            },
            {
              title: t('navigation.mapping'),
              path: `${prefix}/mapping-analytics`,
            },
            {
              title: `${t('navigation.mapping')} v2`,
              path: `${prefix}/mapping-v2`,
              disabled: !FLAGS.MAPPING_V2,
            },
          ],
        },
      ],
    },
    {
      items: [
        {
          title: t('navigation.events'),
          path: `${prefix}/events`,
          icon: CalendarMonthOutlined,
          activeIcon: CalendarMonth,
          disabled: !permissions.events,
        },
      ],
    },
    {
      items: [
        {
          title: t('navigation.prizes'),
          icon: EmojiEventsOutlined,
          activeIcon: EmojiEvents,
          disabled: !permissions.prizes,
          children: [
            {
              title: t('navigation.prizeDraws'),
              path: `${prefix}/prize-draws`,
              disabled: !FLAGS.PRIZE_DRAWS,
            },
          ],
        },
      ],
    },
    mapSection(t, prefix, permissions),
    {
      items: [
        {
          title: t('navigation.setup'),
          icon: DisplaySettingsOutlined,
          activeIcon: DisplaySettings,
          children: [
            {
              title: t('navigation.players'),
              path: `${prefix}/players`,
              disabled: !permissions.players,
            },
            {
              title: t('navigation.teams'),
              path: `${prefix}/teams`,
              disabled: !permissions.teams,
            },
            {
              title: t('navigation.resources'),
              path: `${prefix}/resources`,
              disabled: !permissions.resources,
            },
            {
              title: t('navigation.leaderboards'),
              path: `${prefix}/leaderboards`,
              disabled: !permissions.leaderboards,
            },
            {
              title: t('navigation.settings'),
              path: `${prefix}/general`,
              disabled: !permissions.settings,
            },
          ],
        },
      ],
    },
  ]
}

const useSections = (isOpen: boolean): Navigation[] => {
  const { t } = useTranslation()
  const location = useLocation()
  const { user } = useAuth()
  const { game } = useCurrentGameRoute()
  const matched = matchPath('games/:slug/*', location.pathname)
  const prefix = '/admin'
  const hasGame = useGameAccess()
  const hasGlobalModulesAccess = useHasGlobalModulesAccess()
  const hasAdminManagementAccess = useHasAdminManagementAccess()
  const currentGameId = game?.id

  const hasCurrentGamePermission = useCallback(
    (permission: Permission): boolean => {
      if (user?.isSuperAdmin) {
        return true
      }

      if (!currentGameId) {
        return false
      }

      return hasPermission(user, permission, currentGameId)
    },
    [currentGameId, user]
  )

  const gameNavigationPermissions = useMemo<GameNavigationPermissions>(
    () => ({
      analytics: hasCurrentGamePermission('game:read'),
      cards: hasCurrentGamePermission('card:read'),
      events: hasCurrentGamePermission('event:read'),
      installer: hasCurrentGamePermission('location:install'),
      leaderboards: hasCurrentGamePermission('leaderboard:read'),
      locations: hasCurrentGamePermission('location:read'),
      locationsWrite: hasCurrentGamePermission('location:create'),
      messages: hasCurrentGamePermission('message:read'),
      overview: hasCurrentGamePermission('game:read'),
      players: hasCurrentGamePermission('player:read'),
      prizes: hasCurrentGamePermission('prize:read'),
      resources: hasCurrentGamePermission('game:read'),
      settings: hasCurrentGamePermission('game:update'),
      surveys: hasCurrentGamePermission('survey:read'),
      teams: hasCurrentGamePermission('team:read'),
    }),
    [hasCurrentGamePermission]
  )

  const getNavigation = useMemo(() => {
    if (gameNavigationPermissions.overview && matched) {
      const section = gameSections(
        t,
        `games/${matched.params.slug}`,
        gameNavigationPermissions
      )
      const messageCentreNav = messageCentreItems(
        t,
        `games/${matched.params.slug}`,
        gameNavigationPermissions
      )
      section.splice(2, 0, messageCentreNav)
      return section
    }
    if ((hasAdminManagementAccess || hasGlobalModulesAccess) && !matched) {
      return generalSections(t, prefix, hasGlobalModulesAccess)
    }
    if (matched) {
      const section = gameSectionsPartner(
        t,
        `games/${matched.params.slug}`,
        gameNavigationPermissions
      )
      const messageCentreNav = messageCentreItems(
        t,
        `games/${matched.params.slug}`,
        gameNavigationPermissions
      )
      section.splice(2, 0, messageCentreNav)
      return section
    }

    return !hasGame
      ? generalSectionEmpty(t)
      : generalSectionPartner(t, hasGlobalModulesAccess)
  }, [
    gameNavigationPermissions,
    hasAdminManagementAccess,
    hasGame,
    hasGlobalModulesAccess,
    matched,
    t,
  ])

  const mapNavigationItem = useCallback(
    (items: Navigation['items']) => {
      const visibleItems = filterNavigationItems(items)

      return {
        items: visibleItems.map((item): NavigationItem => ({
          ...item,
          title: isOpen ? item.title : '',
          children: isOpen ? item.children : undefined,
        })),
      }
    },
    [isOpen]
  )

  return useMemo(() => {
    return getNavigation
      .filter(({ disabled }) => !disabled)
      .map(({ items }) => mapNavigationItem(items))
      .filter(({ items }) => items.length > 0)
  }, [getNavigation, mapNavigationItem])
}

export default useSections
